import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { BookOpen, ExternalLink, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Public shared feature card — Iter-110.
 * Reads /public/user-guide/{org}/{feature}?exp=…&token=… (no auth).
 * Used for links generated from the Guide's "Share" button.
 */
export default function GuideShare() {
  const { orgId, code } = useParams();
  const [sp] = useSearchParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const exp = sp.get("exp"); const token = sp.get("token");

  useEffect(() => {
    axios.get(`${API}/public/user-guide/${orgId}/${code}`, { params: { exp, token } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Link invalid"));
  }, [orgId, code, exp, token]);

  if (err) return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 p-6">
      <Card className="rounded-sm p-8 max-w-md text-center">
        <div className="text-4xl mb-4">⚠️</div>
        <div className="text-lg font-heading font-semibold">Cannot open this link</div>
        <div className="text-sm text-muted-foreground mt-2">{err}</div>
      </Card>
    </div>
  );
  if (!data) return <div className="p-10 text-center text-muted-foreground text-sm">Loading shared feature…</div>;

  const feat = data.feature;
  return (
    <div className="min-h-screen bg-muted/20 p-4 md:p-10" data-testid="guide-share">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            <div className="w-8 h-8 rounded-sm bg-accent text-white flex items-center justify-center font-bold">Z</div>
            <div>
              <div className="font-heading font-semibold">Zenith 360 ERP</div>
              <div className="text-[10px] font-mono uppercase text-muted-foreground">// public feature share</div>
            </div>
          </div>
          <div className="text-[10px] font-mono uppercase text-muted-foreground flex items-center gap-1">
            <BookOpen className="w-3 h-3" /> {data.section}
          </div>
        </div>

        <Card className="rounded-sm overflow-hidden">
          {data.has_image && (
            <img src={`${API}/public/user-guide-image/${orgId}/${code}?exp=${exp}&token=${token}`}
                    alt={feat.name}
                    className="w-full border-b border-border" />
          )}
          <div className="p-6 md:p-8 space-y-4">
            <div className="flex items-center gap-2">
              {feat.changed_in && (
                <span className="text-[10px] font-mono uppercase bg-accent/15 text-accent px-1.5 py-0.5 rounded-sm">
                  Changed in {feat.changed_in}
                </span>
              )}
            </div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold">{feat.name}</h1>

            <dl className="grid md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <div>
                <dt className="text-[10px] font-mono uppercase text-accent">Meaning</dt>
                <dd className="mt-1">{feat.meaning}</dd>
              </div>
              <div>
                <dt className="text-[10px] font-mono uppercase text-accent">Requirement</dt>
                <dd className="mt-1">{feat.requirement}</dd>
              </div>
              <div className="md:col-span-2">
                <dt className="text-[10px] font-mono uppercase text-accent">Usage</dt>
                <dd className="mt-1">{feat.usage}</dd>
              </div>
            </dl>

            <div>
              <div className="text-[10px] font-mono uppercase text-accent mb-2">Process steps</div>
              <ol className="list-decimal ml-5 space-y-1.5 text-sm">
                {feat.steps.map((s, i) => <li key={i}>{s}</li>)}
              </ol>
            </div>

            <div className="pt-3 border-t border-dashed border-border flex flex-wrap items-center justify-between gap-2">
              <div className="text-[10px] font-mono text-muted-foreground">
                where in the app · <span className="font-medium text-foreground">{feat.where}</span>
              </div>
              <div className="text-[10px] font-mono text-muted-foreground flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-accent" /> Powered by Zenith 360 ERP
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
